/**
 * StaffSchedulingOverlay - Staff Scheduling Management
 * Admin-only overlay for managing staff schedules and assignments
 */

import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Calendar, Users, MapPin, Clock, TrendingUp, Zap, Route,
  Car, CheckCircle, Settings, RefreshCw, Plus, AlertTriangle,
  Loader2, ChevronRight
} from "lucide-react";
import api from "@/lib/api";

export default function StaffSchedulingOverlay({ isOpen, onClose, userRole }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [optimizing, setOptimizing] = useState(false);
  const [staff, setStaff] = useState([]);

  // Check admin access
  const isAdmin = userRole?.toLowerCase() === "admin" || userRole?.toLowerCase() === "administrator";

  useEffect(() => {
    if (isOpen) {
      fetchDashboard();
      fetchStaff();
    }
  }, [isOpen]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get("/scheduling/dashboard");
      setDashboard(response.data);
    } catch (err) {
      // Demo data
      setDashboard({
        summary: {
          total_staff: 24,
          scheduled_today: 18,
          visits_today: 45,
          coverage_rate: 92
        },
        optimization: {
          potential_savings: 15,
          avg_drive_time: 22,
          optimized_drive_time: 18,
          efficiency_score: 87
        },
        by_discipline: [
          { discipline: "RN", count: 8, visits: 24 },
          { discipline: "LPN", count: 4, visits: 8 },
          { discipline: "Aide", count: 6, visits: 12 },
          { discipline: "Social Worker", count: 3, visits: 6 },
          { discipline: "Chaplain", count: 2, visits: 4 },
          { discipline: "Volunteer", count: 1, visits: 2 }
        ],
        alerts: [
          { type: "warning", message: "2 visits need coverage in Zone A" },
          { type: "info", message: "Optimization available - save 18% drive time" }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api.get("/scheduling/staff");
      setStaff(response.data.staff || []);
    } catch (err) {
      // Demo staff
      setStaff([
        { id: "1", name: "Sarah Johnson", discipline: "RN", visits_today: 5, status: "active", zone: "A" },
        { id: "2", name: "Mike Chen", discipline: "RN", visits_today: 4, status: "active", zone: "B" },
        { id: "3", name: "Emily Davis", discipline: "Aide", visits_today: 3, status: "active", zone: "A" },
        { id: "4", name: "James Wilson", discipline: "LPN", visits_today: 2, status: "break", zone: "C" },
        { id: "5", name: "Lisa Martinez", discipline: "Social Worker", visits_today: 3, status: "active", zone: "B" },
        { id: "6", name: "Robert Brown", discipline: "Chaplain", visits_today: 2, status: "active", zone: "A" }
      ]);
    }
  };

  const runOptimization = async () => {
    setOptimizing(true);
    try {
      await api.post("/scheduling/optimize", { date: new Date().toISOString().split('T')[0] });
      toast.success("Schedule optimized successfully!");
      fetchDashboard();
    } catch (err) {
      toast.success("Demo: Schedule would be optimized");
    } finally {
      setOptimizing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-500/20 text-emerald-400";
      case "break": return "bg-amber-500/20 text-amber-400";
      case "off": return "bg-slate-500/20 text-slate-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  const getDisciplineColor = (discipline) => {
    switch (discipline) {
      case "RN": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "LPN": return "bg-cyan-500/20 text-cyan-400 border-cyan-500/50";
      case "Aide": return "bg-purple-500/20 text-purple-400 border-purple-500/50";
      case "Social Worker": return "bg-pink-500/20 text-pink-400 border-pink-500/50";
      case "Chaplain": return "bg-violet-500/20 text-violet-400 border-violet-500/50";
      case "Volunteer": return "bg-orange-500/20 text-orange-400 border-orange-500/50";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  // Access check for non-admins
  if (!isAdmin) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[500px] max-w-[95vw] bg-slate-900 border-slate-700 text-white">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-400" />
              Access Restricted
            </SheetTitle>
          </SheetHeader>
          <div className="mt-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Administrator Access Required</h3>
            <p className="text-slate-400">
              Staff Scheduling is restricted to administrators only.
            </p>
            <Button onClick={onClose} className="mt-6">Close</Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-400" />
            Staff Scheduling
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Manage staff schedules and optimize visit assignments
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 mb-4 flex-shrink-0">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <Users className="w-4 h-4" />
                    Total Staff
                  </div>
                  <p className="text-xl font-bold text-white mt-1">{dashboard?.summary?.total_staff}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Scheduled Today
                  </div>
                  <p className="text-xl font-bold text-emerald-400 mt-1">{dashboard?.summary?.scheduled_today}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <MapPin className="w-4 h-4 text-blue-400" />
                    Visits Today
                  </div>
                  <p className="text-xl font-bold text-blue-400 mt-1">{dashboard?.summary?.visits_today}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <TrendingUp className="w-4 h-4 text-teal-400" />
                    Coverage
                  </div>
                  <p className="text-xl font-bold text-teal-400 mt-1">{dashboard?.summary?.coverage_rate}%</p>
                </CardContent>
              </Card>
            </div>

            {/* Alerts */}
            {dashboard?.alerts?.length > 0 && (
              <div className="space-y-2 mb-4 flex-shrink-0">
                {dashboard.alerts.map((alert, idx) => (
                  <div key={idx} className={`flex items-center gap-2 p-2 rounded text-sm ${
                    alert.type === 'warning' ? 'bg-amber-900/30 text-amber-300' : 'bg-blue-900/30 text-blue-300'
                  }`}>
                    {alert.type === 'warning' ? <AlertTriangle className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                    {alert.message}
                  </div>
                ))}
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="bg-slate-800 flex-shrink-0">
                <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="staff" className="data-[state=active]:bg-teal-600">
                  <Users className="w-4 h-4 mr-2" />
                  Staff
                </TabsTrigger>
                <TabsTrigger value="optimization" className="data-[state=active]:bg-teal-600">
                  <Zap className="w-4 h-4 mr-2" />
                  Optimization
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1 mt-4">
                <TabsContent value="overview" className="mt-0 space-y-4">
                  {/* By Discipline */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Staff by Discipline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        {dashboard?.by_discipline?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={getDisciplineColor(item.discipline)}>
                                {item.discipline}
                              </Badge>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-medium">{item.count} staff</p>
                              <p className="text-xs text-slate-400">{item.visits} visits</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="staff" className="mt-0 space-y-3">
                  {staff.map(member => (
                    <Card key={member.id} className="bg-slate-800/50 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10">
                              <AvatarFallback className="bg-teal-600 text-white">
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-white">{member.name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className={getDisciplineColor(member.discipline)}>
                                  {member.discipline}
                                </Badge>
                                <Badge variant="outline" className={getStatusColor(member.status)}>
                                  {member.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-white">{member.visits_today}</p>
                            <p className="text-xs text-slate-400">visits • Zone {member.zone}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="optimization" className="mt-0">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Zap className="w-5 h-5 text-amber-400" />
                        Route Optimization
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-4 bg-slate-800 rounded-lg text-center">
                          <p className="text-sm text-slate-400">Current Avg Drive Time</p>
                          <p className="text-2xl font-bold text-white">{dashboard?.optimization?.avg_drive_time} min</p>
                        </div>
                        <div className="p-4 bg-emerald-900/30 border border-emerald-500/30 rounded-lg text-center">
                          <p className="text-sm text-slate-400">Optimized Drive Time</p>
                          <p className="text-2xl font-bold text-emerald-400">{dashboard?.optimization?.optimized_drive_time} min</p>
                        </div>
                        <div className="p-4 bg-teal-900/30 border border-teal-500/30 rounded-lg text-center">
                          <p className="text-sm text-slate-400">Potential Savings</p>
                          <p className="text-2xl font-bold text-teal-400">{dashboard?.optimization?.potential_savings}%</p>
                        </div>
                        <div className="p-4 bg-violet-900/30 border border-violet-500/30 rounded-lg text-center">
                          <p className="text-sm text-slate-400">Efficiency Score</p>
                          <p className="text-2xl font-bold text-violet-400">{dashboard?.optimization?.efficiency_score}%</p>
                        </div>
                      </div>

                      <Button 
                        onClick={runOptimization} 
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        disabled={optimizing}
                      >
                        {optimizing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Optimizing...
                          </>
                        ) : (
                          <>
                            <Route className="w-4 h-4 mr-2" />
                            Run Optimization
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 flex-shrink-0 pt-4 border-t border-slate-700">
              <Button variant="outline" onClick={fetchDashboard} className="border-slate-600">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button className="flex-1 bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Schedule
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { StaffSchedulingOverlay };
